/**
 * Class-Validator Shim for Browser Environments
 *
 * This file provides no-op implementations of class-validator decorators for use in web applications.
 * When bundling for the browser, Vite can be configured to alias 'class-validator' to this file,
 * preventing the validation library from being bundled into the browser application.
 *
 * The decorators do nothing in the browser - they just return no-op decorators.
 * Validation only happens on the server side.
 */

function noop(): PropertyDecorator {
	return () => {
		// No-op
	};
}

function noopWithOptions(_options?: unknown): PropertyDecorator {
	return () => {
		// No-op
	};
}

// Validation decorators - all return no-op property decorators
export const IsString = noopWithOptions;
export const IsNumber = noopWithOptions;
export const IsInt = noopWithOptions;
export const IsBoolean = noopWithOptions;
export const IsArray = noopWithOptions;
export const IsObject = noopWithOptions;
export const IsEnum = (_entity: unknown, _options?: unknown): PropertyDecorator => noop();
export const IsOptional = noopWithOptions;
export const IsNotEmpty = noopWithOptions;
export const IsEmpty = noopWithOptions;
export const IsIn = (_values: ReadonlyArray<unknown>, _options?: unknown): PropertyDecorator => noop();
export const IsNotIn = (_values: ReadonlyArray<unknown>, _options?: unknown): PropertyDecorator => noop();
export const IsDate = noopWithOptions;
export const IsEmail = noopWithOptions;
export const IsUrl = noopWithOptions;
export const IsUUID = noopWithOptions;
export const IsPositive = noopWithOptions;
export const IsNegative = noopWithOptions;
export const Min = (_min: number, _options?: unknown): PropertyDecorator => noop();
export const Max = (_max: number, _options?: unknown): PropertyDecorator => noop();
export const MinLength = (_min: number, _options?: unknown): PropertyDecorator => noop();
export const MaxLength = (_max: number, _options?: unknown): PropertyDecorator => noop();
export const Length = (_min: number, _max?: number, _options?: unknown): PropertyDecorator => noop();
export const Matches = (_pattern: RegExp, _options?: unknown): PropertyDecorator => noop();
export const IsDefined = noopWithOptions;
export const IsInstance = (_targetType: unknown, _options?: unknown): PropertyDecorator => noop();
export const ArrayMinSize = (_min: number, _options?: unknown): PropertyDecorator => noop();
export const ArrayMaxSize = (_max: number, _options?: unknown): PropertyDecorator => noop();
export const ArrayNotEmpty = noopWithOptions;
export const ArrayUnique = noopWithOptions;
export const ValidateNested = noopWithOptions;
export const ValidateIf = (_condition: unknown, _options?: unknown): PropertyDecorator => noop();
export const Validate = (_constraintClass: unknown, _options?: unknown): PropertyDecorator => noop();
export const Allow = noop;
export const IsMilitaryTime = noopWithOptions;
export const IsDateString = noopWithOptions;
export const IsBooleanString = noopWithOptions;
export const IsNumberString = noopWithOptions;
export const Contains = (_seed: string, _options?: unknown): PropertyDecorator => noop();
export const NotContains = (_seed: string, _options?: unknown): PropertyDecorator => noop();
export const IsAlpha = noopWithOptions;
export const IsAlphanumeric = noopWithOptions;
export const IsDecimal = noopWithOptions;
export const IsAscii = noopWithOptions;
export const IsBase64 = noopWithOptions;
export const IsByteLength = noopWithOptions;
export const IsCreditCard = noopWithOptions;
export const IsCurrency = noopWithOptions;
export const IsFQDN = noopWithOptions;
export const IsFullWidth = noopWithOptions;
export const IsHalfWidth = noopWithOptions;
export const IsVariableWidth = noopWithOptions;
export const IsHexColor = noopWithOptions;
export const IsHexadecimal = noopWithOptions;
export const IsIP = noopWithOptions;
export const IsISBN = noopWithOptions;
export const IsISIN = noopWithOptions;
export const IsISO8601 = noopWithOptions;
export const IsJSON = noopWithOptions;
export const IsLowercase = noopWithOptions;
export const IsMobilePhone = noopWithOptions;
export const IsMongoId = noopWithOptions;
export const IsMultibyte = noopWithOptions;
export const IsSurrogatePair = noopWithOptions;
export const IsUppercase = noopWithOptions;
export const IsPhoneNumber = noopWithOptions;
export const IsPostalCode = noopWithOptions;
export const Equals = (_comparison: unknown, _options?: unknown): PropertyDecorator => noop();
export const NotEquals = (_comparison: unknown, _options?: unknown): PropertyDecorator => noop();

// Custom validator decorators
export function ValidatorConstraint(_options?: { name?: string; async?: boolean }): ClassDecorator {
	return (target) => target;
}

// Validation functions (lowercase) - return true in browser (no validation)
export function isEmail(_value: unknown): boolean {
	return true;
}

export function isString(_value: unknown): boolean {
	return true;
}

export function isNumber(_value: unknown): boolean {
	return true;
}

export function isInt(_value: unknown): boolean {
	return true;
}

export function isBoolean(_value: unknown): boolean {
	return true;
}

export function isArray(_value: unknown): boolean {
	return true;
}

export function isObject(_value: unknown): boolean {
	return true;
}

export function isDate(_value: unknown): boolean {
	return true;
}

export function isEnum(_value: unknown, _entity: unknown): boolean {
	return true;
}

export function isEmpty(_value: unknown): boolean {
	return true;
}

export function isNotEmpty(_value: unknown): boolean {
	return true;
}

export function isDefined(_value: unknown): boolean {
	return true;
}

export function isOptional(_value: unknown): boolean {
	return true;
}

export function isUUID(_value: unknown, _version?: unknown): boolean {
	return true;
}

export function isUrl(_value: unknown): boolean {
	return true;
}

export function isJSON(_value: unknown): boolean {
	return true;
}

export function minLength(_value: unknown, _min: number): boolean {
	return true;
}

export function maxLength(_value: unknown, _max: number): boolean {
	return true;
}

export function min(_value: unknown, _min: number): boolean {
	return true;
}

export function max(_value: unknown, _max: number): boolean {
	return true;
}

export function length(_value: unknown, _min: number, _max?: number): boolean {
	return true;
}

export function matches(_value: unknown, _pattern: RegExp): boolean {
	return true;
}

export function contains(_value: unknown, _seed: string): boolean {
	return true;
}

export function notContains(_value: unknown, _seed: string): boolean {
	return true;
}

export function isIn(_value: unknown, _possibleValues: ReadonlyArray<unknown>): boolean {
	return true;
}

export function isNotIn(_value: unknown, _possibleValues: ReadonlyArray<unknown>): boolean {
	return true;
}

export function arrayMinSize(_array: unknown, _min: number): boolean {
	return true;
}

export function arrayMaxSize(_array: unknown, _max: number): boolean {
	return true;
}

export function arrayNotEmpty(_array: unknown): boolean {
	return true;
}

export function arrayUnique(_array: unknown): boolean {
	return true;
}

// Interface for custom validators
export interface ValidatorConstraintInterface {
	validate(value: unknown, validationArguments?: ValidationArguments): boolean | Promise<boolean>;
	defaultMessage?(validationArguments?: ValidationArguments): string;
}

// Types used by class-validator
export interface ValidationOptions {
	message?: string | ((validationArguments: ValidationArguments) => string);
	groups?: Array<string>;
	always?: boolean;
	each?: boolean;
	context?: unknown;
}

export interface ValidationArguments {
	value: unknown;
	constraints: Array<unknown>;
	targetName: string;
	object: object;
	property: string;
}

export interface IsNumberOptions {
	allowNaN?: boolean;
	allowInfinity?: boolean;
	maxDecimalPlaces?: number;
}

// Validation functions (no-ops in browser)
export function validate(_object: object, _options?: unknown): Promise<Array<unknown>> {
	return Promise.resolve([]);
}

export function validateSync(_object: object, _options?: unknown): Array<unknown> {
	return [];
}

export function validateOrReject(_object: object, _options?: unknown): Promise<void> {
	return Promise.resolve();
}